/**
 * useTopTracts.ts — live ranked "Top tracts in view" data (T-222 / BEAD-0040)
 *
 * Reads the tract features the map already holds (`map.querySourceFeatures('tracts')`),
 * ranks them with the pure `rankTopTracts` core, and recomputes on pan/zoom (`moveend`)
 * and when the tract source refetches (`sourcedata`). No new network call — the design's
 * central grounding fact (proposal §2).
 *
 * Only active at the tract tier (proposal §4.1): at state/county zoom the tract source is
 * empty, so the panel shows a "zoom in to rank" hint instead of an empty list.
 */
import { useCallback, useEffect, useState } from 'react'
import { useMap } from '../components/map/MapContext'
import {
  rankTopTracts,
  DEFAULT_LIMIT,
  type SortModeId,
  type RankedRow,
  type RankInput,
} from '../lib/topTracts'
import type { MapTier } from '../lib/mapTiers'

const TRACTS_SOURCE = 'tracts'

export interface UseTopTractsArgs {
  tier: MapTier
  visible: boolean
  sortModeId: SortModeId
  tierFilter?: string[]
  typeFilter?: string[]
  limit?: number
}

export interface TopTractsState {
  rows: RankedRow[]
  totalInView: number
}

const EMPTY: TopTractsState = { rows: [], totalInView: 0 }

export function useTopTracts(args: UseTopTractsArgs): TopTractsState {
  const map = useMap()
  const { tier, visible, sortModeId, tierFilter, typeFilter, limit = DEFAULT_LIMIT } = args
  const active = visible && tier === 'tract'
  const [state, setState] = useState<TopTractsState>(EMPTY)

  // Filter arrays are compared by content (not identity) so a parent re-render that hands
  // back an equal-but-new array doesn't thrash the subscription.
  const tierKey = tierFilter ? tierFilter.join(',') : ''
  const typeKey = typeFilter ? typeFilter.join(',') : ''

  const recompute = useCallback(() => {
    if (!map || !active) {
      setState(EMPTY)
      return
    }
    const feats = map.querySourceFeatures(TRACTS_SOURCE) as unknown as RankInput[]
    setState(rankTopTracts(feats, { sortModeId, tierFilter, typeFilter, limit }))
    // tierFilter/typeFilter intentionally tracked via their content keys below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, active, sortModeId, tierKey, typeKey, limit])

  useEffect(() => {
    recompute()
    if (!map || !active) return
    const onMove = () => recompute()
    const onData = (e: { sourceId?: string }) => {
      if (e.sourceId === TRACTS_SOURCE) recompute()
    }
    map.on('moveend', onMove)
    map.on('sourcedata', onData)
    return () => {
      map.off('moveend', onMove)
      map.off('sourcedata', onData)
    }
  }, [map, active, recompute])

  return state
}
